import { ServiceId } from "@mirage/discovery/id";
import * as services from "@mirage/discovery/services";
import Sentry from "@mirage/shared/sentry";

import { type dash_growth } from "@dropbox/api-v2-client";
import type { APIv2Callable } from "@mirage/service-dbx-api/service";
import type { ConsolaInstance } from "consola";

export type Service = {
  getFssTeamInfo(): Promise<FssTeamInfo | undefined>;
};

export type FssTeamInfoSurvey = {
  surveyQuestionId: string;
  surveyAnswer: string;
};

export type FssTeamInfoSurveyAnswers = {
  surveyId?: number;
  isTeamSurvey?: boolean;
  surveyAnswers?: FssTeamInfoSurvey[];
};

export type FssTeamInfo = {
  teamName: string;
  teamLogo: string;
  surveyAnswers: FssTeamInfoSurveyAnswers[];
};

interface DbxApiServiceContract {
  callApiV2: APIv2Callable;
}

interface LoggingServiceContract {
  tagged: (tag: string) => ConsolaInstance;
}

function convertGetFSSTeamInfoResponseToFssTeamInfo(
  response: dash_growth.GetFSSTeamInfoResponse,
): FssTeamInfo {
  return {
    teamName: response.team_name ?? "",
    teamLogo: response.team_logo ?? "",
    surveyAnswers: (response.survey_answers ?? []).map((survey) => ({
      surveyId: survey.survey_id ? parseInt(survey.survey_id, 10) : undefined,
      isTeamSurvey: survey.is_team_survey ?? false,
      surveyAnswers: (survey.survey_answers ?? []).map((answer) => ({
        surveyQuestionId: answer.survey_question_id ?? "",
        surveyAnswer: answer.survey_answer ?? "",
      })),
    })),
  };
}

export default function service(
  { callApiV2 }: DbxApiServiceContract,
  { tagged }: LoggingServiceContract,
) {
  const logger = tagged("service-fss-team-info");

  async function getFssTeamInfo(): Promise<FssTeamInfo | undefined> {
    try {
      const response = await callApiV2("dashGrowthGetFssTeamInfo", {});
      return convertGetFSSTeamInfoResponseToFssTeamInfo(response);
    } catch (error) {
      logger.error(`getFssTeamInfo - Failed to read FSS team info`);
      Sentry.captureException(error);
      return undefined;
    }
  }

  return services.provide<Service>(
    ServiceId.FSS_TEAM_INFO,
    {
      getFssTeamInfo,
    },
    [ServiceId.DBX_API],
  );
}
